import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { fetchGewerbegebiete } from "@/lib/api-client";
import type { GewerbegebietZone, GewerbegebietResponse } from "@/types/api";

const MAX_SELECTION = 5;

const BADGE_BASE =
  "inline-flex items-center rounded-full px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wide ml-auto shrink-0";

interface GewerbegebietListProps {
  lat: number;
  lng: number;
  radiusKm: number;
  selectedZones: GewerbegebietZone[];
  onSelectedZonesChange: (zones: GewerbegebietZone[]) => void;
}

export function GewerbegebietList({
  lat,
  lng,
  radiusKm,
  selectedZones,
  onSelectedZonesChange,
}: GewerbegebietListProps) {
  const { t } = useTranslation("search");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<GewerbegebietResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // BADGE_CONFIG moved inside component so badge labels can call t()
  const BADGE_CONFIG: Record<string, { label: string; colors: string }> = {
    commercial: {
      label: t("gewerbegebiet.badgeCommercial"),
      colors: "bg-primary/10 text-primary",
    },
    industrial: {
      label: t("gewerbegebiet.badgeIndustrial"),
      colors: "bg-muted-foreground/10 text-muted-foreground",
    },
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      setError(null);
      fetchGewerbegebiete(lat, lng, radiusKm)
        .then((data) => {
          setResponse(data);
          // Clear stale selections — zones from old location/radius may no longer exist
          const newKeys = new Set(
            data.zones.map(
              (z: GewerbegebietZone) => `${z.osm_type}:${z.osm_id}`,
            ),
          );
          const stillValid = selectedZones.filter((z) =>
            newKeys.has(`${z.osm_type}:${z.osm_id}`),
          );
          if (stillValid.length !== selectedZones.length) {
            onSelectedZonesChange(stillValid);
          }
        })
        .catch(() => {
          setError(t("gewerbegebiet.loadError"));
        })
        .finally(() => {
          setLoading(false);
        });
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [lat, lng, radiusKm]);

  function zoneKey(z: GewerbegebietZone): string {
    return `${z.osm_type}:${z.osm_id}`;
  }

  function isSelected(zone: GewerbegebietZone): boolean {
    const key = zoneKey(zone);
    return selectedZones.some((z) => zoneKey(z) === key);
  }

  function toggleZone(zone: GewerbegebietZone) {
    const key = zoneKey(zone);
    if (isSelected(zone)) {
      onSelectedZonesChange(selectedZones.filter((z) => zoneKey(z) !== key));
    } else {
      if (selectedZones.length >= MAX_SELECTION) return;
      onSelectedZonesChange([...selectedZones, zone]);
    }
  }

  function selectAll() {
    if (!response) return;
    onSelectedZonesChange(response.zones.slice(0, MAX_SELECTION));
  }

  function selectNone() {
    onSelectedZonesChange([]);
  }

  const maxReached = selectedZones.length >= MAX_SELECTION;

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("gewerbegebiet.loading")}
      </div>
    );
  }

  if (error) {
    return <p className="py-4 text-sm text-destructive">{error}</p>;
  }

  if (!response) return null;

  if (response.zones.length === 0) {
    return (
      <div className="py-4 space-y-1">
        <p className="text-sm font-medium text-foreground">
          {t("gewerbegebiet.noneFound")}
        </p>
        <p className="text-xs text-muted-foreground">
          {t("gewerbegebiet.noneFoundHint")}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4">
      {/* Header — split-key approach for bold number: render the count element
          separately rather than embedding HTML in the translation string */}
      <div className="flex items-center mb-2">
        <span className="text-[0.8125rem] font-medium text-muted-foreground">
          {response.truncated ? (
            <>
              <span className="font-semibold text-foreground">50</span>{" "}
              {t("gewerbegebiet.shownOfSuffix", {
                total: response.total_found,
              })}
            </>
          ) : (
            <>
              <span className="font-semibold text-foreground">
                {response.count}
              </span>{" "}
              {t("gewerbegebiet.foundSuffix")}
            </>
          )}
        </span>
      </div>

      {/* Zone list */}
      <div
        className="border border-border rounded-[var(--radius)] overflow-hidden"
        style={{
          maxHeight: "240px",
          overflowY: "auto",
          scrollbarWidth: "thin",
        }}
      >
        {response.zones.map((zone) => {
          const selected = isSelected(zone);
          const disabled = !selected && maxReached;
          return (
            <div
              key={zoneKey(zone)}
              onClick={() => !disabled && toggleZone(zone)}
              className={[
                "flex items-center gap-2.5 px-3 py-2.5 border-b border-border last:border-b-0 cursor-pointer transition-colors",
                selected ? "bg-primary/5" : "bg-card hover:bg-muted/50",
                disabled ? "opacity-40 pointer-events-none" : "",
              ].join(" ")}
            >
              {/* Custom checkbox */}
              <div
                className={[
                  "flex items-center justify-center shrink-0 w-4 h-4 rounded border-2 transition-colors",
                  selected
                    ? "bg-primary border-primary"
                    : "bg-transparent border-border",
                ].join(" ")}
              >
                {selected && (
                  <svg
                    viewBox="0 0 10 8"
                    className="w-2.5 h-2 fill-none stroke-white stroke-[1.8]"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="1,4 3.5,6.5 9,1" />
                  </svg>
                )}
              </div>

              {/* Zone name */}
              <span className="text-[0.8125rem] font-medium flex-1 min-w-0 truncate">
                {zone.name}
              </span>

              {/* Badge */}
              <span
                className={`${BADGE_BASE} ${(BADGE_CONFIG[zone.landuse] ?? BADGE_CONFIG.industrial).colors}`}
              >
                {(BADGE_CONFIG[zone.landuse] ?? BADGE_CONFIG.industrial).label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Truncation hint */}
      {response.truncated && (
        <p className="text-xs text-muted-foreground mt-1.5">
          {t("gewerbegebiet.truncationHint")}
        </p>
      )}

      {/* Footer — split-key approach for bold count */}
      <div className="flex items-center justify-between mt-2.5">
        <span className="text-[0.8125rem] text-muted-foreground">
          {maxReached ? (
            <span className="text-accent font-medium">
              {t("gewerbegebiet.maxReached")}
            </span>
          ) : (
            <>
              <strong>{selectedZones.length}</strong>{" "}
              {t("gewerbegebiet.selectedCountSuffix", {
                max: MAX_SELECTION,
              })}
            </>
          )}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={selectAll}
            className="text-xs text-primary underline underline-offset-2 cursor-pointer hover:opacity-80 transition-opacity"
          >
            {t("gewerbegebiet.selectAll")}
          </button>
          <button
            type="button"
            onClick={selectNone}
            className="text-xs text-muted-foreground underline underline-offset-2 cursor-pointer hover:text-foreground transition-colors"
          >
            {t("gewerbegebiet.selectNone")}
          </button>
        </div>
      </div>
    </div>
  );
}
